import type { BenefitInputs, RuleAction, RuleEngineRunHistoryEntry } from '../types/rulesEngine'

const STORAGE_KEY = 'healthlynk_rules_engine_history_v1'
const MAX_ENTRIES = 100

export const RULES_ENGINE_HISTORY_UPDATED_EVENT = 'healthlynk:rules-engine-history-updated'

function notifyUpdated() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(RULES_ENGINE_HISTORY_UPDATED_EVENT))
}

function loadHistory(): RuleEngineRunHistoryEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as RuleEngineRunHistoryEntry[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveHistory(entries: RuleEngineRunHistoryEntry[]) {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  notifyUpdated()
}

export function listRuleEngineRunHistory(): RuleEngineRunHistoryEntry[] {
  return loadHistory().sort((a, b) => b.ranAt.localeCompare(a.ranAt))
}

export type RecordRunHistoryInput = {
  patientId: string
  patientName: string
  memberId?: string
  payor?: string
  ruleSetId: string
  ruleSetName: string
  outcome: RuleAction
  triggerRule: string
  message: string | null
  benefitInputs: BenefitInputs
  rulesEvaluated: number
}

export function listRuleEngineRunHistoryForPatient(patientId: string): RuleEngineRunHistoryEntry[] {
  const needle = patientId.trim()
  if (!needle) return []
  return listRuleEngineRunHistory().filter((entry) => entry.patientId === needle)
}

export function clearRuleEngineRunHistoryForPatient(patientId: string): void {
  const needle = patientId.trim()
  if (!needle) return
  saveHistory(loadHistory().filter((entry) => entry.patientId !== needle))
}

export function recordRuleEngineRun(input: RecordRunHistoryInput): RuleEngineRunHistoryEntry {
  const entry: RuleEngineRunHistoryEntry = {
    id: `run-${Date.now()}`,
    patientId: input.patientId,
    patientName: input.patientName,
    memberId: input.memberId,
    payor: input.payor,
    ruleSetId: input.ruleSetId,
    ruleSetName: input.ruleSetName,
    outcome: input.outcome,
    triggerRule: input.triggerRule,
    message: input.message,
    ranAt: new Date().toISOString(),
    benefitInputs: { ...input.benefitInputs },
    rulesEvaluated: input.rulesEvaluated,
  }

  const next = [entry, ...loadHistory()].slice(0, MAX_ENTRIES)
  saveHistory(next)
  return entry
}

export function deleteRuleEngineRunHistoryEntry(id: string): void {
  saveHistory(loadHistory().filter((e) => e.id !== id))
}

export function clearRuleEngineRunHistory(): void {
  saveHistory([])
}
